import React, { useState, useEffect, useMemo } from 'react';
import { Link, router, usePage } from '@inertiajs/react';
import StaffLayout from '@/layouts/StaffLayout';
import ThemeWrapper from '@/components/ThemeWrapper';
import Avatar from '@/components/common/Avatar';
import useRoutes from '@/hooks/useRoutes';
import { User } from '@/types';
import { GraduationCap, Search, X, ChevronLeft, ChevronRight } from 'lucide-react';

interface Course {
    id: number;
    title: string;
}

interface Pagination {
    page: number;
    per_page: number;
    total: number;
    num_pages: number;
    has_previous: boolean;
    has_next: boolean;
    previous_page: number | null;
    next_page: number | null;
}

interface StudentsIndexProps {
    students: User[];
    courses: Course[];
    pagination: Pagination;
}

const IndexContent: React.FC<StudentsIndexProps> = ({ students, courses, pagination }) => {
    const { route } = useRoutes();
    const { url } = usePage();
    const urlParams = new URLSearchParams(url.split('?')[1] || '');
    const initialSearch = urlParams.get('search') || '';

    const [searchQuery, setSearchQuery] = useState(initialSearch);
    const [selectedCourse, setSelectedCourse] = useState<string>('');

    // Filter students based on course filter (client-side, current page only)
    // Search is now handled server-side
    const filteredStudents = useMemo(() => {
        let filtered = students;

        // Apply course filter (client-side for now)
        if (selectedCourse) {
            const courseId = Number(selectedCourse);
            filtered = filtered.filter((student) =>
                student.courses?.some((course: Course) => course.id === courseId)
            );
        }

        return filtered;
    }, [students, selectedCourse]);

    // Debounced search handler
    useEffect(() => {
        const timer = setTimeout(() => {
            const params: { search?: string; page?: number } = {};

            if (searchQuery.trim()) {
                params.search = searchQuery.trim();
            }

            // Reset to page 1 when search changes
            if (searchQuery.trim() !== initialSearch) {
                params.page = 1;
            }

            // Only make request if search has changed
            if (searchQuery.trim() !== initialSearch) {
                router.get(route('staff-students').toString(), params, {
                    preserveState: true,
                    preserveScroll: false,
                });
            }
        }, 300); // 300ms debounce

        return () => clearTimeout(timer);
    }, [searchQuery]);

    const handlePageChange = (page: number) => {
        const params: { page: number; search?: string } = { page };

        if (searchQuery.trim()) {
            params.search = searchQuery.trim();
        }

        router.get(route('staff-students').toString(), params, {
            preserveState: true,
            preserveScroll: true,
        });
    };

    return (
        <StaffLayout>
            <div className="mx-auto max-w-7xl">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">
                        Estudiantes
                    </h1>
                    <p className="mt-2 text-gray-600">
                        Gestión de estudiantes de la academia
                    </p>
                </div>

                {/* Search and Filters */}
                <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    {/* Search Bar */}
                    <div className="relative flex-1 sm:max-w-md">
                        <Search
                            className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400"
                            size={18}
                        />
                        <input
                            type="text"
                            placeholder="Buscar por nombre o email..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-10 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                        {searchQuery && (
                            <button
                                type="button"
                                onClick={() => setSearchQuery('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 transform text-gray-400 hover:text-gray-600"
                                aria-label="Limpiar búsqueda"
                            >
                                <X size={18} />
                            </button>
                        )}
                    </div>

                    {/* Course Filter */}
                    <div className="sm:w-64">
                        <select
                            value={selectedCourse}
                            onChange={(e) => setSelectedCourse(e.target.value)}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                            <option value="">Todos los cursos</option>
                            {courses.map((course) => (
                                <option key={course.id} value={course.id}>
                                    {course.title}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Results Count */}
                {pagination.total > 0 && (
                    <div className="mb-4 text-sm text-gray-600">
                        Mostrando {filteredStudents.length} de {pagination.total} estudiantes
                    </div>
                )}

                {students.length === 0 && !searchQuery.trim() ? (
                    <div className="rounded-lg bg-white p-12 text-center shadow">
                        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                            <GraduationCap size={32} className="text-gray-400" />
                        </div>
                        <h3 className="mb-2 text-lg font-semibold text-gray-900">
                            No hay estudiantes
                        </h3>
                        <p className="text-gray-600">
                            No hay estudiantes inscritos en los cursos de tu academia.
                        </p>
                    </div>
                ) : students.length === 0 || filteredStudents.length === 0 ? (
                    <div className="rounded-lg bg-white p-12 text-center shadow">
                        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                            <Search size={32} className="text-gray-400" />
                        </div>
                        <h3 className="mb-2 text-lg font-semibold text-gray-900">
                            No se encontraron resultados
                        </h3>
                        <p className="text-gray-600">
                            Intenta ajustar los filtros de búsqueda.
                        </p>
                    </div>
                ) : (
                    <div className="overflow-hidden rounded-lg bg-white shadow">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                        Nombre
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                        Email
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                        Academia
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                        Inscripciones
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                        Puntos
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                        Rango
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                        Discord
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                        Estado
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                                {filteredStudents.map((student) => (
                                    <tr
                                        key={student.id}
                                        className="hover:bg-gray-50"
                                    >
                                        <td className="whitespace-nowrap px-6 py-4">
                                            <Link
                                                href={route('staff-students-detail', { student_id: student.id }).toString()}
                                                className="flex items-center"
                                            >
                                                <div className="mr-3">
                                                    <Avatar user={student} size="md" />
                                                </div>
                                                <div className="text-sm font-medium text-gray-900 hover:text-blue-600">
                                                    {student.full_name}
                                                </div>
                                            </Link>
                                        </td>
                                        <td className="whitespace-nowrap px-6 py-4">
                                            <div className="text-sm text-gray-500">
                                                {student.email}
                                            </div>
                                        </td>
                                        <td className="whitespace-nowrap px-6 py-4">
                                            <div className="text-sm text-gray-900">
                                                {student.current_academy?.name || '-'}
                                            </div>
                                        </td>
                                        <td className="whitespace-nowrap px-6 py-4">
                                            <div className="text-sm text-gray-900">
                                                {student.enrollments_count || 0}
                                            </div>
                                        </td>
                                        <td className="whitespace-nowrap px-6 py-4">
                                            <div className="text-sm text-gray-900">
                                                {student.points || 0}
                                            </div>
                                        </td>
                                        <td className="whitespace-nowrap px-6 py-4">
                                            <div className="text-sm text-gray-900">
                                                {student.rank?.name || '-'}
                                            </div>
                                        </td>
                                        <td className="whitespace-nowrap px-6 py-4">
                                            <div className="text-sm text-gray-500">
                                                {student.discord_username || '-'}
                                            </div>
                                        </td>
                                        <td className="whitespace-nowrap px-6 py-4">
                                            {student.is_active ? (
                                                <span className="inline-flex rounded-full bg-green-100 px-2 text-xs font-semibold leading-5 text-green-800">
                                                    Activo
                                                </span>
                                            ) : (
                                                <span className="inline-flex rounded-full bg-red-100 px-2 text-xs font-semibold leading-5 text-red-800">
                                                    Inactivo
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Pagination */}
                        {pagination.num_pages > 1 && (
                            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-6 py-4">
                                <div className="flex flex-1 justify-between sm:hidden">
                                    <button
                                        onClick={() => handlePageChange(pagination.previous_page!)}
                                        disabled={!pagination.has_previous}
                                        className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        Anterior
                                    </button>
                                    <button
                                        onClick={() => handlePageChange(pagination.next_page!)}
                                        disabled={!pagination.has_next}
                                        className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        Siguiente
                                    </button>
                                </div>
                                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                                    <div>
                                        <p className="text-sm text-gray-700">
                                            Página <span className="font-medium">{pagination.page}</span> de{' '}
                                            <span className="font-medium">{pagination.num_pages}</span>
                                        </p>
                                    </div>
                                    <div>
                                        <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                                            <button
                                                onClick={() => handlePageChange(pagination.previous_page!)}
                                                disabled={!pagination.has_previous}
                                                className="relative inline-flex items-center rounded-l-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                <span className="sr-only">Anterior</span>
                                                <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                                            </button>
                                            <button
                                                onClick={() => handlePageChange(pagination.next_page!)}
                                                disabled={!pagination.has_next}
                                                className="relative inline-flex items-center rounded-r-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                <span className="sr-only">Siguiente</span>
                                                <ChevronRight className="h-5 w-5" aria-hidden="true" />
                                            </button>
                                        </nav>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </StaffLayout>
    );
};

const Index: React.FC<StudentsIndexProps> = (props) => {
    return (
        <ThemeWrapper>
            <IndexContent {...props} />
        </ThemeWrapper>
    );
};

export default Index;
